import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";
import { X, Crop as CropIcon, RefreshCw } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Portal from "@/components/Portal";

const RATIOS = [
  { label: "4:5 (Product)", value: 4 / 5 },
  { label: "1:1 (Square)", value: 1 },
  { label: "3:4 (Portrait)", value: 3 / 4 },
  { label: "16:9 (Wide)", value: 16 / 9 },
  { label: "Free", value: null },
];

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

// Pre-loads an image to catch broken URLs or CORS issues before mounting Cropper.
function useImageLoader(src) {
  const [status, setStatus] = useState(src ? "loading" : "idle");
  useEffect(() => {
    if (!src) { setStatus("idle"); return; }
    setStatus("loading");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setStatus("loaded");
    img.onerror = () => setStatus("error");
    img.src = src;
    return () => { img.onload = null; img.onerror = null; };
  }, [src]);
  return status;
}

// Accepts either a File (freshly picked) OR an imageUrl (existing image).
// Crop is performed server-side via POST /api/admin/crop.
export default function ImageCropModal({ file, imageUrl, onClose, onConfirm }) {
  const [src, setSrc] = useState(imageUrl || null);
  const [preparing, setPreparing] = useState(!!file);
  const [error, setError] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4 / 5);
  const [cropPx, setCropPx] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgStatus = useImageLoader(src);

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // If a File was passed, upload it first so the backend can crop from the URL.
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    (async () => {
      setPreparing(true);
      setError(null);
      try {
        const url = await uploadFile(file);
        if (!cancelled) setSrc(url);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
      } finally {
        if (!cancelled) setPreparing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  const onCropComplete = useCallback((_, areaPx) => setCropPx(areaPx), []);

  const confirm = async () => {
    if (!cropPx || !src) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/crop`, {
        image_url: src,
        x: Math.round(cropPx.x),
        y: Math.round(cropPx.y),
        width: Math.round(cropPx.width),
        height: Math.round(cropPx.height),
      });
      const newUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      toast.success("Image cropped");
      onConfirm(newUrl);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Crop failed");
    } finally {
      setBusy(false);
    }
  };

  const showCropper = !preparing && !error && src && imgStatus === "loaded";
  const isLoading = preparing || imgStatus === "loading";
  const isError = !!error || imgStatus === "error";
  const canConfirm = !!cropPx && showCropper && !busy;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[80] bg-black/90 overflow-y-auto"
        data-testid="image-crop-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
      <div
        className="w-full max-w-4xl mx-auto my-6 p-6"
        style={{ background: "#141414", border: "1px solid var(--hairline)" }}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="font-micro text-[#B8895C] mb-1 flex items-center gap-2"><CropIcon size={12} strokeWidth={1.5} /> Crop &amp; Adjust</div>
            <div className="font-serif text-[#EDE4D3] text-2xl">Frame your image</div>
          </div>
          <button onClick={onClose} className="text-[#EDE4D3] hover:text-[#B8895C]" data-testid="crop-close" aria-label="Close">
            <X size={20} strokeWidth={1} />
          </button>
        </div>

        <div className="relative w-full h-[420px]" style={{ background: "#0B0B0C" }} data-testid="crop-stage">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" data-testid="crop-preparing">
              <RefreshCw size={20} strokeWidth={1} className="text-[#B8895C] animate-spin" />
              <div className="font-micro text-[#8A8680]">
                {preparing ? "Uploading image…" : "Loading image…"}
              </div>
            </div>
          )}
          {isError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="crop-error">
              <div className="font-serif text-[#EDE4D3] text-lg">Couldn&apos;t load this image</div>
              <div className="font-micro text-[#8A8680] max-w-md">{error || "The image URL could not be reached. Try uploading it fresh."}</div>
              <button onClick={onClose} className="btn-ghost mt-3">Close</button>
            </div>
          )}
          {showCropper && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
              showGrid={true}
              restrictPosition={true}
            />
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="crop-ratios">
          {RATIOS.map((r) => (
            <button
              key={r.label}
              onClick={() => setAspect(r.value)}
              disabled={!showCropper}
              className={`chip ${aspect === r.value ? "active" : ""}`}
              data-testid={`crop-ratio-${r.label.split(" ")[0]}`}
              style={!showCropper ? { opacity: 0.4, cursor: "not-allowed" } : {}}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-4">
          <span className="font-micro text-[#8A8680] whitespace-nowrap">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={!showCropper}
            className="flex-1 accent-[#B8895C]"
            data-testid="crop-zoom"
          />
        </div>

        <div className="mt-8 flex gap-4 justify-end">
          <button onClick={onClose} className="btn-ghost" data-testid="crop-cancel">Cancel</button>
          <button
            onClick={confirm}
            disabled={!canConfirm}
            className="btn-bronze"
            data-testid="crop-confirm"
            style={!canConfirm ? { opacity: 0.4, cursor: "not-allowed" } : {}}
          >
            {busy ? "Cropping…" : "Use this image"}
          </button>
        </div>
      </div>
      </div>
    </Portal>
  );
}
